import type { TokenCredential } from "@azure/identity";
import { LocalCliError } from "./errors.js";

export interface AuthedJsonArgs {
  credential: TokenCredential;
  scope: string;
  method: "GET" | "POST" | "PUT" | "DELETE";
  url: string;
  body?: unknown;
  /** HTTP statuses to treat as success-with-undefined instead of throwing (e.g. 404 for existence checks). */
  okStatuses?: number[];
}

/** Result wrapper exposing the status for callers that branch on 404 etc. */
export interface AuthedJsonResult<T> { status: number; data: T | undefined }

export async function authedJsonResult<T = unknown>(args: AuthedJsonArgs): Promise<AuthedJsonResult<T>> {
  const token = await args.credential.getToken(args.scope);
  if (!token) {
    throw new LocalCliError({
      code: "AUTH_NULL_TOKEN",
      message: `Azure returned no token for scope ${args.scope}.`,
      hint: "Run 'az login' and retry.",
    });
  }
  const res = await fetch(args.url, {
    method: args.method,
    headers: {
      Authorization: `Bearer ${token.token}`,
      ...(args.body !== undefined ? { "Content-Type": "application/json" } : {}),
    },
    ...(args.body !== undefined ? { body: JSON.stringify(args.body) } : {}),
  });

  const ok = res.ok || (args.okStatuses?.includes(res.status) ?? false);
  if (!ok) {
    const text = await res.text().catch(() => "");
    throw new LocalCliError({
      code: "AZURE_HTTP_ERROR",
      message: `${args.method} ${args.url} failed: ${res.status.toString()} ${res.statusText}${text ? ` — ${text.slice(0, 400)}` : ""}`,
    });
  }

  const raw = await res.text();
  const data = raw ? (JSON.parse(raw) as T) : undefined;
  return { status: res.status, data };
}

/** Convenience: returns the parsed body (undefined for empty), throwing on non-2xx. */
export async function authedJson<T = unknown>(args: AuthedJsonArgs): Promise<T | undefined> {
  return (await authedJsonResult<T>(args)).data;
}
